import React, { useState } from 'react';
import axios from 'axios';
import { API_BASE } from '../api/config';

type State = 'idle' | 'loading' | 'success' | 'error';

const NewsletterSignup: React.FC = () => {
  const [email, setEmail] = useState('');
  const [consentGiven, setConsentGiven] = useState(false);
  const [state, setState] = useState<State>('idle');
  const [errorMessage, setErrorMessage] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email || !consentGiven) return;

    setState('loading');
    setErrorMessage('');
    try {
      await axios.post(`${API_BASE}/api/Newsletter/subscribe`, {
        email,
        consentGiven,
      });
      setState('success');
    } catch (err: any) {
      const msg = err?.response?.data?.message ?? 'Something went wrong. Please try again.';
      setErrorMessage(msg);
      setState('error');
    }
  };

  if (state === 'success') {
    return (
      <div className="flex items-start gap-3 bg-white/10 rounded-xl p-4">
        <span className="material-symbols-outlined text-secondary mt-0.5" data-icon="check_circle">check_circle</span>
        <div>
          <p className="text-white text-sm font-semibold">You're subscribed.</p>
          <p className="text-on-primary-container text-sm mt-0.5">
            You can unsubscribe at any time using the link in any email we send you.
          </p>
        </div>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-3">
      <div className="flex gap-2">
        <input
          type="email"
          required
          value={email}
          onChange={e => setEmail(e.target.value)}
          placeholder="Your email address"
          className="bg-white/10 border border-white/20 rounded-lg px-4 py-2 w-full text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-secondary text-sm"
        />
        <button
          type="submit"
          disabled={!consentGiven || !email || state === 'loading'}
          className="bg-secondary text-on-secondary px-4 py-2 rounded-lg text-sm font-semibold disabled:opacity-40 disabled:cursor-not-allowed hover:bg-secondary/90 active:scale-95 transition-all flex-shrink-0"
        >
          {state === 'loading' ? (
            <span className="material-symbols-outlined text-[20px] animate-spin">progress_activity</span>
          ) : (
            <span className="material-symbols-outlined text-[20px]" data-icon="send">send</span>
          )}
        </button>
      </div>

      <label className="flex items-start gap-2.5 cursor-pointer">
        <input
          type="checkbox"
          checked={consentGiven}
          onChange={e => setConsentGiven(e.target.checked)}
          className="mt-0.5 h-3.5 w-3.5 flex-shrink-0 rounded border-white/30 bg-white/10 text-secondary focus:ring-secondary"
        />
        <span className="text-xs text-on-primary-container leading-relaxed">
          I agree to receive occasional platform updates and building tips from Buildon.
          You can unsubscribe at any time. See our{' '}
          <a href="/privacy-policy" className="underline hover:text-white">Privacy Policy</a>.
        </span>
      </label>

      {state === 'error' && (
        <p className="text-xs text-red-300">{errorMessage}</p>
      )}
    </form>
  );
};

export default NewsletterSignup;
